import { useEffect } from 'react';
import { COMPANY } from '../legal/company';

interface PageMeta {
  /**
   * The complete document title, brand included — written out per page rather
   * than assembled here, since each one is worded for what people search for.
   * Omitted on the home page, which keeps the site-wide title from
   * public/index.html so the tab and the shared link preview read the same.
   */
  title?: string;
  description?: string;
  /** Path this page should be indexed under, e.g. '/what-you-get'. Omit to leave the canonical alone. */
  path?: string;
  /** Pages that should stay out of search: the login page and everything signed in. */
  index?: boolean;
}

/** Writes into the tag public/index.html ships, creating it only if a build ever drops it. */
function setMeta(selector: string, attribute: 'name' | 'property', key: string, content: string) {
  let tag = document.head.querySelector<HTMLMetaElement>(selector);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

/**
 * Per-route title, description, canonical and robots for this single-page app.
 *
 * public/index.html carries the site-wide version of all of these, which is what
 * social scrapers read — they don't run our JavaScript, so a shared link always
 * previews as the site rather than the specific page. This hook is for the
 * browser tab, for a visitor's history and bookmarks, and for Google, which does
 * render the page before indexing it.
 *
 * Every value is restored when the page unmounts, so navigating away can never
 * leave one route's description attached to another.
 */
export function usePageMeta({ title, description, path, index = true }: PageMeta): void {
  useEffect(() => {
    const restore: Array<() => void> = [];

    /** Sets a tag for the life of this page, queueing the old value for unmount. */
    const write = (selector: string, attribute: 'name' | 'property', key: string, content: string) => {
      const tag = document.head.querySelector<HTMLMetaElement>(selector);
      const previous = tag?.getAttribute('content') ?? null;
      setMeta(selector, attribute, key, content);
      if (tag && previous !== null) restore.push(() => tag.setAttribute('content', previous));
    };

    if (title) {
      const previousTitle = document.title;
      document.title = title;
      restore.push(() => {
        document.title = previousTitle;
      });

      // The sharing titles too: some link unfurlers (and Slack in particular)
      // re-read the DOM after render rather than the raw HTML.
      write('meta[property="og:title"]', 'property', 'og:title', title);
      write('meta[name="twitter:title"]', 'name', 'twitter:title', title);
    }

    if (description) {
      write('meta[name="description"]', 'name', 'description', description);
      write('meta[property="og:description"]', 'property', 'og:description', description);
      write('meta[name="twitter:description"]', 'name', 'twitter:description', description);
    }

    // `follow` rather than `nofollow`: keeping a page out of results is the point,
    // and there is nothing to gain from telling crawlers to ignore our own links.
    if (!index) write('meta[name="robots"]', 'name', 'robots', 'noindex, follow');

    const canonicalTag = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (path && canonicalTag) {
      const previousCanonical = canonicalTag.getAttribute('href');
      const canonicalUrl = `${COMPANY.siteUrl}${path === '/' ? '/' : path}`;
      canonicalTag.setAttribute('href', canonicalUrl);
      if (previousCanonical !== null) {
        restore.push(() => canonicalTag.setAttribute('href', previousCanonical));
      }
      write('meta[property="og:url"]', 'property', 'og:url', canonicalUrl);
    }

    return () => restore.forEach((undo) => undo());
  }, [title, description, path, index]);
}

export default usePageMeta;
